/**
 * The portal home page's notification bell — top-right, beside `UserMenu`.
 *
 * One feed: an application or layer shared with the signed-in user, being
 * added to a group, or their own role being changed (see `services.notify`
 * on the backend, called from the handful of grant/admin endpoints that can
 * happen TO someone). Polled rather than pushed — this app has no websocket
 * channel, and a badge that is at most `POLL_INTERVAL_MS` stale is a fair
 * trade against standing up one just for this.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, CheckCheck, Layers, Share2, ShieldCheck, UsersRound } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  notificationService,
  type AppNotification,
  type NotificationKind,
} from '../../services/notificationService';

const KIND_ICON: Record<NotificationKind, LucideIcon> = {
  module_shared: Share2,
  layer_shared: Layers,
  group_added: UsersRound,
  role_changed: ShieldCheck,
};

const POLL_INTERVAL_MS = 45_000;

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const refresh = async () => {
    try {
      const result = await notificationService.list();
      setItems(result.items);
      setUnreadCount(result.unreadCount);
    } catch {
      // A missed poll is not worth surfacing — the next one tries again, and
      // nothing else on the page depends on this succeeding.
    }
  };

  // The badge count has to stay live even while the panel is closed, so this
  // polls independently of `open`.
  useEffect(() => {
    void refresh();
    const handle = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, []);

  // A fresh read the moment the panel opens, so a notification that arrived
  // between polls is not missed just because the tab was sitting idle.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    void refresh().finally(() => setLoading(false));
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = triggerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const markRead = async (notification: AppNotification) => {
    if (notification.read) return;
    // Optimistic — a person opening a panel and clicking an item expects it
    // to look read immediately, not after a round trip.
    setItems((prev) => prev.map((entry) => (entry.id === notification.id ? { ...entry, read: true } : entry)));
    setUnreadCount((count) => Math.max(0, count - 1));
    try {
      await notificationService.markRead(notification.id);
    } catch {
      void refresh();
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((entry) => ({ ...entry, read: true })));
    setUnreadCount(0);
    try {
      await notificationService.markAllRead();
    } catch {
      void refresh();
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl transition hover:bg-white/10"
        style={{ border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.10)' }}
      >
        <Bell className="h-4 w-4 text-white/85" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9.5px] font-bold leading-none text-white"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label="Notifications"
            className="gl-menu fixed z-[6000] flex max-h-[70vh] w-80 flex-col overflow-hidden"
            style={{
              top: position?.top ?? -9999,
              right: position?.right ?? -9999,
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-100 px-3.5 py-2.5">
              <span className="text-[12.5px] font-semibold text-slate-800">Notifications</span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => void markAllRead()}
                  className="flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </button>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading && items.length === 0 ? (
                <p className="px-3.5 py-8 text-center text-xs text-slate-400">Loading…</p>
              ) : items.length === 0 ? (
                <p className="px-3.5 py-8 text-center text-xs text-slate-400">
                  Nothing yet — an application or layer shared with you, or being added to a group,
                  will show up here.
                </p>
              ) : (
                <ul>
                  {items.map((notification) => {
                    const Icon = KIND_ICON[notification.kind] ?? Bell;
                    return (
                      <li key={notification.id}>
                        <button
                          type="button"
                          onClick={() => void markRead(notification)}
                          className={`flex w-full items-start gap-2.5 border-b border-slate-50 px-3.5 py-2.5 text-left transition hover:bg-slate-50 ${
                            notification.read ? '' : 'bg-blue-50/50'
                          }`}
                        >
                          <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
                            <Icon className="h-3.5 w-3.5 text-slate-500" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[12.5px] font-medium leading-snug text-slate-800">
                              {notification.title}
                            </span>
                            {notification.body && (
                              <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
                                {notification.body}
                              </span>
                            )}
                            <span className="mt-1 block text-[10.5px] text-slate-400">
                              {relativeTime(notification.createdAt)}
                            </span>
                          </span>
                          {!notification.read && (
                            <span
                              aria-hidden="true"
                              className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500"
                            />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
